import { useBlockBrowserClose } from '@/hooks/useBlockBrowserClose';
import { PolotnoStoreKeys, useMedia, useMediaDelete, useMediaUpload, usePolotnoState } from '@/pages/AdvancedEditor/hooks';
import { uploadFile } from '@/services/api';
import styled from '@emotion/styled';
import CircularProgress from '@mui/material/CircularProgress';
import { observer } from 'mobx-react-lite';
import { nanoid } from 'nanoid';
import { SectionContainer } from '../../common/SectionContainer';
import { StyledText } from '../../common/SectionTitle';
import { UploadingMediaEntity } from '../types';
import { MediaGrid } from './MediaGrid';
import { MediaUpload } from './MediaUpload';

const MediaListContainer = styled.div`
    padding: 20px 20px 0 !important;
    height: 100%;
    overflow: hidden;
    gap: 24px;
    display: flex;
    flex-direction: column;
`;

const StyledCircularProgress = styled(CircularProgress)`
    margin: 20px auto;
`;

export const MediaList = observer(() => {
    const { data: externalMedia = [], isLoading } = useMedia();
    const [uploadingMedia] = usePolotnoState(PolotnoStoreKeys.uploadingMedia);

    useBlockBrowserClose(() => !!uploadingMedia.size);

    const { mutateAsync: deleteMedia } = useMediaDelete();
    const { mutateAsync: uploadMedia } = useMediaUpload();

    const handleInputChange = async (media: UploadingMediaEntity) => {
        uploadingMedia.set(media.id, media);

        // Change file name to avoid character encoding issues
        const fileExtension = media.file.name.split('.').pop();
        const newFileName = `${nanoid()}.${fileExtension}`;
        const newFile = new File([media.file], newFileName, { type: media.file.type });

        const src = await uploadFile(newFile).catch(() => {
            uploadingMedia.delete(media.id);
        });

        if (!src) return;

        const metadata = {
            ...(media?.duration ? { duration_ms: media?.duration?.toString() } : {}),
            width: media.width.toString(),
            height: media.height.toString(),
        };

        const uploadResult = await uploadMedia({ src, metadata }).catch(() => {
            uploadingMedia.delete(media.id);
        });

        if (!uploadResult) return;

        uploadingMedia.delete(media.id);
        uploadingMedia.set(uploadResult.id, { ...media, id: uploadResult.id });
    };

    const handleDelete = async (mediaId: string) => {
        uploadingMedia.delete(mediaId);
        await deleteMedia(mediaId);
    };

    const loadingMedia = Array.from(uploadingMedia.values())
        .filter(ui => !externalMedia.some(ei => ei.id === ui.id))
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt));

    const media = [...loadingMedia, ...externalMedia];

    return (
        <MediaListContainer>
            {/*Upload section*/}
            <SectionContainer>
                <MediaUpload onInputChange={handleInputChange} />
            </SectionContainer>

            {/*Media section*/}
            <SectionContainer height={`calc(100% - ${150 + 24 + 5}px)`}>
                {!!media.length && <StyledText>Media library</StyledText>}

                {isLoading ? <StyledCircularProgress /> : <MediaGrid media={media} onDelete={handleDelete} />}
            </SectionContainer>
        </MediaListContainer>
    );
});
